import { distanceToString } from "utilities/format";
import { sortDevicesByColumnThenRow } from "utilities/geometric";
import constants from "toolConstants/conferenceroomDesigner/constants";
import { getExpansionCeilingIsFocused, getExpansionSpeakerIsFocused } from "toolConstants/conferenceroomDesigner/logic";
import { values, startCase, findKey, every } from 'lodash'

const reportDocument = {
  metadata: {
    title: 'Workplace Designer Report',
    author: 'Biamp',
    subject: 'Workplace Designer results report',
    creator: 'Biamp'
  },

  body: [
    {
      id: 'header',
      content: [
        {
          element: 'header',
          content: 'Workplace Designer'
        },
        {
          element: 'headerSubTitle',
          content: 'project.name'
        },
      ]
    },
    {
      id: 'project',
      content: [
        {
          element: 'stringField',
          label: 'Design Engineer',
          content: 'project.designer'
        },
        {
          element: 'valueField',
          label: 'Date Generated',
          content: new Date().toString().split(/\(/g)[0]
        }
      ]
    },
  ],

  areas: (state) => values(state.areas).reduce((result, area) => {
    result.push(
      {
        id: area.id,
        content: [
          {
            element: 'title',
            content: area.name,
          },
          {
            element: 'subTitle',
            content: 'ROOM DETAILS',
          },
          {
            element: 'valueField',
            label: 'Room Dimensions',
            content: (state) => `${distanceToString(state.areas[area.id].dimensions.length, state.isMeter)} x ${distanceToString(state.areas[area.id].dimensions.width, state.isMeter)} x ${distanceToString(state.areas[area.id].ceilingHeight.min, state.isMeter)}`
          },
          {
            element: 'valueField',
            label: 'Table shape',
            content: (state) => {
              try {
                const currentArea = state.areas[area.id];
                return currentArea?.table?.shape ? startCase(currentArea.table.shape) : 'None';
              } catch (error) {
                return 'None';
              }
            }
          },
          {
            element: 'valueField',
            label: 'Table Dimensions',
            content: (state) => {
              try {
                const currentArea = state.areas[area.id];
                if (!currentArea?.table?.shape || !currentArea?.table?.size) return 'N/A';
                const length = distanceToString(currentArea.table.size.length, state.isMeter);
                const width = currentArea.table.shape !== 'round' ? ` x ${distanceToString(currentArea.table.size.width, state.isMeter)}` : '';
                return `${length}${width}`;
              } catch (error) {
                return 'N/A';
              }
            }
          },
          {
            element: 'linkField',
            label: 'Acoustics',
            content: (state) => {
              try {
                const currentArea = state.areas[area.id];
                return { src: 'http://bia.mp/RoomAcoustics', text: startCase(currentArea?.acoustics || 'good') };
              } catch (error) {
                return { text: 'Good' };
              }
            }
          },
          {
            element: 'stringField',
            label: 'Mic Style',
            content: (state) => {
              try {
                const currentArea = state.areas[area.id];
                const model = currentArea?.microphones?.model || 'tcm_x';
                const { micCategories } = constants.equipment;
                if (micCategories.BAR.has(model)) return 'Bar';
                if (micCategories.TABLETOP.has(model)) return 'Tabletop';
                if (micCategories.PENDANT.has(model)) return 'Pendant';
                if (micCategories.CEILING_TILE.has(model)) return 'Ceiling Tile';
                return 'Ceiling';
              } catch (error) {
                return 'Ceiling';
              }
            }
          },
          {
            element: 'valueField',
            label: 'Mic Coverage',
            content: (state) => {
              try {
                const currentArea = state.areas[area.id];
                // Bar + ceiling-expansion configs: the mic Coverage toggle drives
                // `expansionMicCoverage`, NOT the (forced-focused) bar mic's own coverage. Use the
                // same resolver the placement/visualizer use so the report matches the canvas —
                // otherwise switching expansion mics to Full still reads Focused here.
                if (currentArea?.expansionMicType === 'ceiling') {
                  return getExpansionCeilingIsFocused(currentArea) ? 'Focused' : 'Full Room';
                }
                // The Coverage toggle is hidden after a manual move/add/delete
                // (hideCoverageToggle) since the layout no longer matches either preset —
                // report that as N/A rather than a stale Focused/Full Room value.
                if (currentArea?.microphones?.hideCoverageToggle) return 'N/A';
                const coverage = currentArea?.microphones?.coverage;
                if (!coverage) return 'Focused';
                return !every(coverage, c => !c) ? startCase(findKey(coverage)) : 'Focused';
              } catch (error) {
                return 'Focused';
              }
            }
          },
          {
            element: 'valueField',
            label: 'Speaker Coverage',
            content: (state) => {
              try {
                const currentArea = state.areas[area.id];
                // Bar + expansion-speaker configs (ceiling OR pendant): the Coverage toggle drives
                // `expansionSpeakerCoverage`, NOT the (bar) primary speaker's coverage. Use the same
                // resolver the placement uses so the report matches the canvas — otherwise switching
                // expansion speakers to Full still reads Focused here.
                if (currentArea?.expansionSpeakerType === 'ceiling' || currentArea?.expansionSpeakerType === 'pendant') {
                  return getExpansionSpeakerIsFocused(currentArea) ? 'Focused' : 'Full Room';
                }
                // See Mic Coverage above — same hideCoverageToggle convention for speakers.
                if (currentArea?.speakers?.hideCoverageToggle) return 'N/A';
                const coverage = currentArea?.speakers?.coverage;
                if (!coverage) return 'Focused';
                return !every(coverage, c => !c) ? startCase(findKey(coverage)) : 'Focused';
              } catch (error) {
                return 'Focused';
              }
            }
          }
        ]
      },
      {
        id: `${area.id}-microphones`,
        content: [
          {
            element: 'subTitle',
            content: 'MICROPHONE COVERAGE'
          },
          {
            element: 'plot',
            areaId: area.id,
            content: (state) => state,
            device: 'microphones',
            constants,
          },

        ]
      },
      {
        id: 'micPositioningDetail',
        wrap: true,
        content: [
          {
            element: 'subTitle',
            content: (state) => {
              const model = state.areas[area.id].microphones?.model || 'tcm_x';
              const isBarMic = constants.equipment.micCategories.BAR.has(model);
              const isCeilingTile = constants.equipment.micCategories.CEILING_TILE.has(model);
              const prefix = isBarMic ? 'CONFERENCE BAR' : isCeilingTile ? 'CEILING TILE' : 'MICROPHONE';
              return `${prefix} POSITIONING DETAIL`
            }
          },
          {
            element: 'table',
            content: (state) => {
              const model = state.areas[area.id].microphones?.model || 'tcm_x';
              const isBarMic = constants.equipment.micCategories.BAR.has(model);
              const isTableMic = constants.equipment.micCategories.TABLETOP.has(model);
              // Append expansion mic positions as additional line items in the same table.
              // Each expansion mic carries the same `model` / `modelName` / `location` shape
              // as a primary mic (set by addExpansionMic / the calc), so the row renderer
              // doesn't need to branch.
              // Ceiling expansion mics (dcm_x, or legacy tcm_x on older saved designs) are also
              // MIRRORED into microphones.layout by roomCalculations' allMicCoords, so a plain
              // concat listed every one of them twice in the PDF — and, when the mirror and the
              // authoritative slot disagreed on count (port-budget trim, or a preserved layout on
              // a view change), by an inconsistent amount. Filter the mirrored copies out and take
              // the count from `expansionMicrophones.layout` only, matching the web page's
              // Microphone locations tab (getPositionDetail in containerSelectors.ts) and the
              // canvas (MuiDevices.tsx).
              const isCeilingExpansion = state.areas[area.id].expansionMicType === 'ceiling';
              const isCeilingExpansionModel = (m) => m === 'dcm_x' || m === 'tcm_x';
              const rawPrimaryLayout = state.areas[area.id].microphones?.layout ?? [];
              const primaryLayout = isCeilingExpansion
                ? rawPrimaryLayout.filter((d) => !isCeilingExpansionModel(d?.model))
                : rawPrimaryLayout;
              // Column-then-row ordered for the same reason getPositionDetail orders: the stored
              // layout is in #micPath traversal (ring) order, and whether the port-cap regroups it
              // into column order depends on whether the mic count is even or odd — so the PDF's row
              // numbering would otherwise flip convention with the acoustic tier. The shared helper
              // is tolerance-based so mirrored pairs (identical x up to float noise) don't get
              // ordered bottom-first; it returns a new array, keeping this off the frozen state.
              const expansionLayout = sortDevicesByColumnThenRow(
                state.areas[area.id].expansionMicrophones?.layout ?? [],
              );
              // CEILING expansion mics are pole/flush mounted at their own configurable height,
              // which is unrelated to the primary's — and the primary in a bar + expansion design
              // is by definition a bar (FIXED_HEIGHT), so the table-wide `isBarMic` suppression
              // would blank a height the installer actually needs. `_showHeight` overrides the
              // table-wide rule for these rows only; `_heightFallback` supplies the group height
              // for any row whose `location.z` didn't survive a save round-trip. Table expansion
              // (ttm_x) is deliberately NOT marked — it sits on the table at a fixed height, the
              // same reason a primary tabletop mic's height is suppressed.
              // Matches getPositionDetail (containerSelectors.ts) row for row.
              const expansionSlot = state.areas[area.id].expansionMicrophones;
              const expansionRows = isCeilingExpansion
                ? expansionLayout.map((d) => ({
                    ...d,
                    _showHeight: true,
                    _heightFallback: expansionSlot?.height,
                  }))
                : expansionLayout;
              return {
                listData: [...primaryLayout, ...expansionRows],
                isMeter: state.isMeter,
                adjust: isBarMic,
                dimensions: state.areas[area.id].dimensions,
                microphoneHeight: state.areas[area.id].microphones?.height,
                isBarMic,
                isTableMic
              }
            },
            constants: constants.equipmentLists.microphonePositioning
          }
        ]
      },
      {
        id: `${area.id}-speakers`,
        content: [
          {
            element: 'subTitle',
            content: 'SPEAKER COVERAGE'
          },
          {
            element: 'plot',
            areaId: area.id,
            content: (state) => state,
            device: 'speakers',
            constants,
          },
        ]
      },
      {
        id: 'spkrPositioningDetail',
        wrap: true,
        content: [
          {
            element: 'subTitle',
            content: (state) => {
              const speakers = state.areas[area.id].speakers;
              const spkrModel = speakers.model;
              const modelName = speakers.modelName || 'Desono C-IC6';
              const { isBarSpkr = false } = constants.equipment.speakers[spkrModel] || {};
              const title = isBarSpkr ? 'CONFERENCE BAR' : `SPEAKER (${modelName})`;
              return `${title} POSITIONING DETAIL`
            }
          },
          {
            element: 'table',
            content: (state) => {
              const spkrModel = state.areas[area.id].speakers.model;
              const { isBarSpkr = false, isCeilingSpkr = false, isPendantSpkr = false, isWallSpkr = false } = constants.equipment.speakers[spkrModel];
              // Same pattern as mics — expansion speakers append as additional rows.
              const primaryLayout = state.areas[area.id].speakers?.layout ?? [];
              const expansionLayout = state.areas[area.id].expansionSpeakers?.layout ?? [];
              return {
                listData: [...primaryLayout, ...expansionLayout],
                isMeter: state.isMeter,
                adjust: isBarSpkr,
                isCeilingSpkr,
                isPendantSpkr,
                isWallSpkr,
                dimensions: state.areas[area.id].dimensions,
              };
            },
            constants: constants.equipmentLists.speakerPositioning
          }
        ]
      },
    );
    // Check if area has a camera, if not bypass this section 
    if (area.cameras.hasCamera) {
      result.push(
        {
          id: `${area.id}-cameras`,
          content: [
            {
              element: 'subTitle',
              content: 'CAMERA COVERAGE'
            },
            {
              element: 'plot',
              areaId: area.id,
              content: (state) => state,
              device: 'cameras',
              constants,
            },
          ]
        },
        {
          id: 'cameraPositioningDetail',
          wrap: true,
          content: [
            {
              element: 'subTitle',
              content: (state) => {
                const cameraModel = state.areas[area.id].cameras.model;
                const cameraConfig = cameraModel && constants.equipment.cameras[cameraModel] 
                  ? constants.equipment.cameras[cameraModel] 
                  : { isBarCamera: false };
                const { isBarCamera = false } = cameraConfig;
                return `${isBarCamera ? 'CONFERENCE BAR' : 'CAMERA'} POSITIONING DETAIL`
              }
            },
            {
              element: 'table',
              content: (state) => {
                const dimensions = state.areas[area.id].dimensions;
                const { shape, transform: tableCenter } = state.areas[area.id].table;
                const center = Boolean(shape) ? tableCenter : {};
                const cameraModel = state.areas[area.id].cameras.model;
                const cameraConfig = cameraModel && constants.equipment.cameras[cameraModel]
                  ? constants.equipment.cameras[cameraModel]
                  : { isBarCamera: false };
                const isBarCamera = cameraConfig.isBarCamera ?? false;
                return { listData: state.areas[area.id].cameras.layout, isMeter: state.isMeter, adjust: true, dimensions, center, isBarCamera }
              },
              constants: constants.equipmentLists.cameraPositioning
            }
          ]
        },
      );

    };
    result.push(
      {
        id: 'equipmentList',
        wrap: true,
        content: [
          {
            element: 'subTitle',
            content: 'EQUIPMENT LIST'
          },
          {
            element: 'table',
            content: (state) => {
              const equipmentListData = state.areas[area.id].equipmentList || {};
              
              // Handle the case where equipmentList is an array (from calculateEquipmentTable)
              if (Array.isArray(equipmentListData)) {
                const tableData = equipmentListData.map(item => {
                  // Convert each equipment item to a row format
                  // Expected structure: { qty, product: { name, link }, description }
                  return {
                    'Quantity': item.qty || '',
                    'Product': item.product ? item.product.name || '' : '',
                    'Description': item.description || ''
                  };
                });
                
                return { 
                  listData: { 
                    tableData: tableData
                  } 
                };
              } else {
                // Handle the original object format with tableData property
                const { tableData = [] } = equipmentListData;
                
                // Process tableData to handle objects with name/link properties
                const processedTableData = tableData.map(item => {
                  if (!item) return [];
                  
                  // Convert each cell value, handling objects with name/link properties
                  return Object.keys(item).reduce((acc, key) => {
                    const cellValue = item[key];
                    
                    // Handle null/undefined values
                    if (cellValue === null || cellValue === undefined) {
                      acc[key] = '';
                      return acc;
                    }
                    
                    // Handle objects with name/link properties
                    if (typeof cellValue === 'object' && cellValue !== null) {
                      if ('name' in cellValue && 'link' in cellValue) {
                        acc[key] = String(cellValue.name);
                        return acc;
                      }
                    }
                    
                    // Convert other values to string
                    acc[key] = String(cellValue);
                    return acc;
                  }, {});
                });
                
                return { 
                  listData: { 
                    ...equipmentListData, 
                    tableData: processedTableData 
                  } 
                };
              }
            },
            constants: constants.equipmentLists.summary
          }
        ]
      },
    )
    return result
  }, []),
};

export default reportDocument;
